import { z } from "zod";
import { optionalEnum, optionalUuid } from "./_helpers/optional";
import { ExternalIdEntrySchema } from "./external-id.schema";

// Warranty 3-layer model (ADR-0047 / ADV-1168). DB shapes mirror
// supabase/migrations/20260525191001_adv_1168_warranty_terms_obligations_claims.sql:
//   - warranty_terms  → template (WarrantyTermSchema)
//   - warranties      → obligation/instance (WarrantySchema — reclaims the name)
//   - warranty_claims → remediation work (WarrantyClaimSchema — the OLD warranties table)

// ---------------------------------------------------------------------------
// Enums (TEXT + CHECK in the DB; house style is z.enum over exported arrays)
// ---------------------------------------------------------------------------

export const warrantyTermCategories = [
  "workmanship",
  "manufacturer",
  "extended",
] as const;

export const warrantyTermTypes = [
  "labor",
  "parts",
  "expenses",
  "labor_parts",
  "all",
] as const;

export const warrantyDurationUnits = ["days", "months", "years"] as const;

export const warrantyStartBases = [
  "install_date",
  "completion_date",
  "purchase_date",
  "registration_date",
] as const;

export const warrantyExchangeTypes = [
  "repair",
  "replace",
  "loaner",
  "upgrade",
  "substitute",
] as const;

export const warrantyStatuses = [
  "pending",
  "active",
  "expired",
  "cancelled",
  "voided",
  "transferred",
] as const;

// ===========================================================================
// Layer 3 — Warranty CLAIM (remediation work; renamed from the old Warranty)
// ===========================================================================

export const WarrantyClaimSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_id: z.string().uuid().describe("Account"),
  business_id: z.string().uuid().describe("Business"),
  warranty_status: z.string().nullable().describe("Warranty Status"),
  warranty_issues: z.record(z.unknown()).nullable().describe("Warranty Issues"),
  urgency: z.string().nullable().describe("Urgency"),
  scope: z.record(z.unknown()).nullable().describe("Scope"),
  project_type: z.string().nullable().describe("Project Type"),
  production_start: z.string().nullable().describe("Production Start"),
  production_end: z.string().nullable().describe("Production End"),
  warranty_complete: z.string().nullable().describe("Warranty Complete"),
  reschedule_date: z.string().nullable().describe("Reschedule Date"),
  initial_report: z.string().nullable().describe("Initial Report"),
  warranty_id: z.string().uuid().nullable().describe("Warranty"),
  job_id: z.string().uuid().nullable().describe("Job"),
  crew_id: z.string().uuid().nullable().describe("Crew"),
  contact_role_id: z.string().uuid().nullable().describe("Contact Role"),
  tech_role_id: z.string().uuid().nullable().describe("Tech"),
  proposal_id: z.string().uuid().nullable().describe("Proposal"),
  opportunity_id: z.string().uuid().nullable().describe("Opportunity"),
  territory_id: z.string().uuid().nullable().describe("Territory"),
  team_pay_amount: z.number().nullable().describe("Team Pay Amount"),
  customer_fee_amount: z.number().nullable().describe("Customer Fee Amount"),
  details: z.record(z.unknown()).nullable().describe("Details"),
  custom_fields: z
    .record(z.unknown())
    .nullable()
    .optional()
    .describe("Custom Fields"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type WarrantyClaim = z.infer<typeof WarrantyClaimSchema>;

export const WarrantyClaimInsertSchema = z.object({
  warranty_status: z.string().optional().describe("Warranty Status"),
  warranty_issues: z.any().optional().describe("Warranty Issues"),
  urgency: z.string().optional().describe("Urgency"),
  scope: z.any().optional().describe("Scope"),
  project_type: z.string().optional().describe("Project Type"),
  production_start: z.string().optional().describe("Production Start"),
  production_end: z.string().optional().describe("Production End"),
  warranty_complete: z.string().optional().describe("Warranty Complete"),
  reschedule_date: z.string().optional().describe("Reschedule Date"),
  initial_report: z.string().optional().describe("Initial Report"),
  warranty_id: optionalUuid().describe("Warranty"),
  job_id: optionalUuid().describe("Job"),
  crew_id: optionalUuid().describe("Crew"),
  contact_role_id: optionalUuid().describe("Contact Role"),
  tech_role_id: optionalUuid().describe("Tech"),
  proposal_id: optionalUuid().describe("Proposal"),
  opportunity_id: optionalUuid().describe("Opportunity"),
  territory_id: optionalUuid().describe("Territory"),
  team_pay_amount: z.number().optional().describe("Team Pay Amount"),
  customer_fee_amount: z.number().optional().describe("Customer Fee Amount"),
  details: z.any().optional().describe("Details"),
  custom_fields: z.any().optional().describe("Custom Fields"),
});

export const WarrantyClaimUpdateSchema = WarrantyClaimInsertSchema.partial();

export const WarrantyClaimQuerySchema = z.object({
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  warranty_status: z.string().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  warranty_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  job_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  proposal_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  opportunity_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  contact_role_id: z.string().uuid().optional(),
  search: z.string().optional().describe("Search project_type"),
});

// ===========================================================================
// Layer 1 — Warranty TERM (reusable coverage template)
// ===========================================================================

export const WarrantyTermSchema = z.object({
  id: z.string().uuid().describe("Warranty Term"),
  account_id: z.string().uuid().describe("Account"),
  business_id: z.string().uuid().describe("Business"),
  name: z.string().describe("Name"),
  description: z.string().nullable().describe("Description"),
  warranty_category: z
    .enum(warrantyTermCategories)
    .describe("Warranty Category"),
  warranty_type: z.enum(warrantyTermTypes).describe("Warranty Type"),
  labor_covered_pct: z.number().nullable().describe("Labor Covered %"),
  parts_covered_pct: z.number().nullable().describe("Parts Covered %"),
  expenses_covered_pct: z.number().nullable().describe("Expenses Covered %"),
  duration_value: z.number().int().positive().describe("Duration Value"),
  duration_unit: z.enum(warrantyDurationUnits).describe("Duration Unit"),
  effective_start_basis: z
    .enum(warrantyStartBases)
    .describe("Effective Start Basis"),
  exchange_type: z
    .enum(warrantyExchangeTypes)
    .nullable()
    .describe("Exchange Type"),
  is_transferable: z.boolean().describe("Transferable"),
  is_proratable: z.boolean().describe("Proratable"),
  is_voidable: z.boolean().describe("Voidable"),
  exclusion_details: z.string().nullable().describe("Exclusion Details"),
  // ADV-1207 / ADR-0051 §1 + ADR-0052: the backing provider is now the
  // manufacturer / warranty_provider trade-partner CONTACT ROLE, replacing v1's
  // provider_account_id → core.accounts (dropped in
  // 20260526144512_adv_1207_warranty_term_products_and_provider_role.sql).
  provider_contact_role_id: z
    .string()
    .uuid()
    .nullable()
    .describe("Provider Contact Role"),
  // ADV-1207 / ADR-0051 §1: the single warranty_terms.product_id is gone. A term
  // is manufacturer-scoped by default and attaches to SPECIFIC products via the
  // finance.warranty_term_products join (WarrantyTermProductSchema below).
  is_active: z.boolean().describe("Active"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type WarrantyTerm = z.infer<typeof WarrantyTermSchema>;

export const WarrantyTermInsertSchema = z.object({
  name: z.string().describe("Name"),
  description: z.string().optional().describe("Description"),
  warranty_category: z
    .enum(warrantyTermCategories)
    .describe("Warranty Category"),
  warranty_type: z.enum(warrantyTermTypes).describe("Warranty Type"),
  labor_covered_pct: z.number().optional().describe("Labor Covered %"),
  parts_covered_pct: z.number().optional().describe("Parts Covered %"),
  expenses_covered_pct: z.number().optional().describe("Expenses Covered %"),
  duration_value: z.number().int().positive().describe("Duration Value"),
  duration_unit: z.enum(warrantyDurationUnits).describe("Duration Unit"),
  effective_start_basis: z
    .enum(warrantyStartBases)
    .describe("Effective Start Basis"),
  exchange_type: optionalEnum(warrantyExchangeTypes).describe("Exchange Type"),
  is_transferable: z.boolean().default(false).describe("Transferable"),
  is_proratable: z.boolean().default(false).describe("Proratable"),
  is_voidable: z.boolean().default(false).describe("Voidable"),
  exclusion_details: z.string().optional().describe("Exclusion Details"),
  // ADV-1207 / ADR-0051 §1: provider re-pointed to the trade-partner contact
  // role; the per-term product_id is replaced by the warranty_term_products join.
  provider_contact_role_id: optionalUuid().describe("Provider Contact Role"),
  is_active: z.boolean().default(true).describe("Active"),
});

export const WarrantyTermUpdateSchema = WarrantyTermInsertSchema.partial();

// Parse `?is_active=true|false` from query strings literally — Zod's
// `z.coerce.boolean()` would follow JS truthiness and treat the string
// "false" as truthy, silently breaking `?is_active=false` requests.
const QueryBooleanSchema = z.preprocess((v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    if (v === "true") return true;
    if (v === "false") return false;
  }
  return v;
}, z.boolean());

export const WarrantyTermQuerySchema = z.object({
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  warranty_category: z.enum(warrantyTermCategories).optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  warranty_type: z.enum(warrantyTermTypes).optional(),
  // ADV-1207 / ADR-0051 §1: product_id is no longer a column on warranty_terms;
  // "terms covering this product" now resolves through finance.warranty_term_products.
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  product_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — URL query params arrive as strings; coerce "true"/"false" safely
  is_active: QueryBooleanSchema.optional(),
  search: z.string().optional().describe("Search name"),
});

// ===========================================================================
// Layer 1b — Warranty TERM ↔ PRODUCT join (ADV-1207 / ADR-0051 §1)
// finance.warranty_term_products: attaches a (manufacturer-scoped) term to
// SPECIFIC products. A term with no rows here covers every product of its
// provider_contact_role_id; rows here narrow it to a curated set. Replaces the
// dropped single warranty_terms.product_id.
// ===========================================================================

export const WarrantyTermProductSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_id: z.string().uuid().describe("Account"),
  business_id: z.string().uuid().describe("Business"),
  warranty_term_id: z.string().uuid().describe("Warranty Term"),
  product_id: z.string().uuid().describe("Product"),
  created_at: z.string().describe("Created At"),
});
export type WarrantyTermProduct = z.infer<typeof WarrantyTermProductSchema>;

// Insert: account_id/business_id are stamped from tenant context; the row is
// just the (term, product) pair. The DB BEFORE-trigger
// finance.validate_warranty_term_product_manufacturer enforces that the
// product's manufacturer matches the term's provider when both are set.
export const WarrantyTermProductInsertSchema = z.object({
  warranty_term_id: z.string().uuid().describe("Warranty Term"),
  product_id: z.string().uuid().describe("Product"),
});

export const WarrantyTermProductQuerySchema = z.object({
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  warranty_term_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  product_id: z.string().uuid().optional(),
});

// ===========================================================================
// Layer 2 — Warranty OBLIGATION (term stamped onto a customer/property/job)
// ===========================================================================

export const WarrantySchema = z.object({
  id: z.string().uuid().describe("Warranty"),
  account_id: z.string().uuid().describe("Account"),
  business_id: z.string().uuid().describe("Business"),
  warranty_term_id: z.string().uuid().describe("Warranty Term"),
  customer_contact_role_id: z.string().uuid().describe("Customer Contact Role"),
  property_id: z.string().uuid().nullable().describe("Property"),
  location_id: z.string().uuid().nullable().describe("Location"),
  opportunity_id: z.string().uuid().nullable().describe("Opportunity"),
  job_id: z.string().uuid().nullable().describe("Job"),
  effective_start_date: z.string().nullable().describe("Effective Start Date"),
  // DB-computed by finance.compute_warranty_expiration(); never set from app code.
  expiration_date: z.string().nullable().describe("Expiration Date"),
  status: z.enum(warrantyStatuses).describe("Status"),
  // ADV-1208 (ADR-0051 §2): point-in-time coverage snapshot copied from the term
  // by finance.snapshot_warranty_coverage() on insert. The obligation — not the
  // term — is the coverage source of truth; never set from app code (DB-populated,
  // like expiration_date), so these are absent from WarrantyInsertSchema.
  warranty_category: z
    .enum(warrantyTermCategories)
    .describe("Warranty Category"),
  warranty_type: z.enum(warrantyTermTypes).describe("Warranty Type"),
  duration_value: z.number().int().positive().describe("Duration Value"),
  duration_unit: z.enum(warrantyDurationUnits).describe("Duration Unit"),
  effective_start_basis: z
    .enum(warrantyStartBases)
    .describe("Effective Start Basis"),
  exchange_type: z
    .enum(warrantyExchangeTypes)
    .nullable()
    .describe("Exchange Type"),
  is_proratable: z.boolean().describe("Proratable"),
  is_voidable: z.boolean().describe("Voidable"),
  exclusion_details: z.string().nullable().describe("Exclusion Details"),
  labor_covered_pct: z.number().nullable().describe("Labor Covered %"),
  parts_covered_pct: z.number().nullable().describe("Parts Covered %"),
  expenses_covered_pct: z.number().nullable().describe("Expenses Covered %"),
  is_transferable: z.boolean().nullable().describe("Transferable"),
  // ADV-1207 / ADR-0051 §1 + ADR-0052: backing provider re-pointed from v1's
  // provider_account_id → core.accounts (dropped) to the trade-partner contact
  // role that names who to file a claim with.
  provider_contact_role_id: z
    .string()
    .uuid()
    .nullable()
    .describe("Provider Contact Role"),
  deductible_amount: z.number().nullable().describe("Deductible Amount"),
  previous_holder_contact_id: z
    .string()
    .uuid()
    .nullable()
    .describe("Previous Holder Contact"),
  transferred_at: z.string().nullable().describe("Transferred At"),
  voided_reason: z.string().nullable().describe("Voided Reason"),
  voided_at: z.string().nullable().describe("Voided At"),
  registration_number: z.string().nullable().describe("Registration Number"),
  registered_at: z.string().nullable().describe("Registered At"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type Warranty = z.infer<typeof WarrantySchema>;

// expiration_date is intentionally absent: it is computed by a DB trigger from
// effective_start_date + the linked term duration (database-first-calculations rule).
export const WarrantyInsertSchema = z.object({
  warranty_term_id: z.string().uuid().describe("Warranty Term"),
  customer_contact_role_id: z.string().uuid().describe("Customer Contact Role"),
  property_id: optionalUuid().describe("Property"),
  location_id: optionalUuid().describe("Location"),
  opportunity_id: optionalUuid().describe("Opportunity"),
  job_id: optionalUuid().describe("Job"),
  effective_start_date: z.string().optional().describe("Effective Start Date"),
  status: z.enum(warrantyStatuses).default("pending").describe("Status"),
  labor_covered_pct: z.number().optional().describe("Labor Covered %"),
  parts_covered_pct: z.number().optional().describe("Parts Covered %"),
  expenses_covered_pct: z.number().optional().describe("Expenses Covered %"),
  is_transferable: z.boolean().optional().describe("Transferable"),
  // ADV-1207 / ADR-0051 §1 + ADR-0052: replaces v1 provider_account_id. Unlike
  // the §2 coverage snapshot fields, the provider is NOT copied from the term by
  // finance.snapshot_warranty_coverage (it is absent from that COALESCE list),
  // so it stays app-settable here.
  provider_contact_role_id: optionalUuid().describe("Provider Contact Role"),
  deductible_amount: z.number().optional().describe("Deductible Amount"),
  previous_holder_contact_id: optionalUuid().describe(
    "Previous Holder Contact",
  ),
  transferred_at: z.string().optional().describe("Transferred At"),
  voided_reason: z.string().optional().describe("Voided Reason"),
  voided_at: z.string().optional().describe("Voided At"),
  registration_number: z.string().optional().describe("Registration Number"),
  registered_at: z.string().optional().describe("Registered At"),
});

export const WarrantyUpdateSchema = WarrantyInsertSchema.partial();

export const WarrantyQuerySchema = z.object({
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  warranty_term_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  customer_contact_role_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  status: z.enum(warrantyStatuses).optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  job_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  opportunity_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  property_id: z.string().uuid().optional(),
  search: z.string().optional().describe("Search registration_number"),
});
